import { useState, type ReactNode } from "react";
import { Download, File as FileIcon, FileSpreadsheet, FileText, Image as ImageIcon, X } from "lucide-react";

export type FileAttachmentData = {
  url: string;
  fileName: string;
  mimeType?: string;
};

type PreviewKind = "image" | "pdf" | null;

function previewKind(mimeType?: string): PreviewKind {
  if (!mimeType) return null;
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  return null;
}

function isWord(mimeType?: string): boolean {
  return !!mimeType && (mimeType === "application/msword" || mimeType.includes("wordprocessingml"));
}

function isExcel(mimeType?: string): boolean {
  return !!mimeType && (mimeType === "application/vnd.ms-excel" || mimeType.includes("spreadsheetml"));
}

/** Icône, couleurs et libellé de type — un jeu par famille de fichier. */
function fileTypeStyle(mimeType?: string): {
  Icon: typeof FileIcon;
  iconColor: string;
  badgeBg: string;
  label: string;
} {
  const kind = previewKind(mimeType);
  if (kind === "image") {
    return { Icon: ImageIcon, iconColor: "text-purple-600", badgeBg: "bg-purple-50", label: "Image" };
  }
  if (kind === "pdf") {
    return { Icon: FileText, iconColor: "text-red-600", badgeBg: "bg-red-50", label: "Document PDF" };
  }
  if (isExcel(mimeType)) {
    return { Icon: FileSpreadsheet, iconColor: "text-emerald-600", badgeBg: "bg-emerald-50", label: "Feuille Excel" };
  }
  if (isWord(mimeType)) {
    return { Icon: FileText, iconColor: "text-blue-600", badgeBg: "bg-blue-50", label: "Document Word" };
  }
  return { Icon: FileIcon, iconColor: "text-gray-500", badgeBg: "bg-gray-100", label: "Fichier" };
}

/**
 * Carte cliquable pour une pièce jointe : icône + couleur selon le type de fichier,
 * ouvre un aperçu adapté (image, PDF, ou téléchargement direct sinon).
 */
export default function FileAttachmentLink({
  file,
  className,
  trailing,
}: {
  file: FileAttachmentData;
  className?: string;
  /** Contenu additionnel affiché à droite de la carte (badge de statut, bouton de suppression...). */
  trailing?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { Icon, iconColor, badgeBg, label } = fileTypeStyle(file.mimeType);
  const kind = previewKind(file.mimeType);

  return (
    <>
      <div
        className={
          className ??
          "flex w-full items-center gap-3 rounded-[10px] border bg-white px-3 py-2.5"
        }
        style={{ borderColor: "rgba(0,0,0,0.08)" }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] ${badgeBg}`}>
            <Icon size={20} className={iconColor} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-[#0B1F16]">{file.fileName}</span>
            <span className="block text-xs text-[#0B1F16]/50">{label}</span>
          </span>
        </button>
        {trailing}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
          aria-labelledby="file-attachment-title"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[12px] bg-white text-[#0B1F16] shadow-xl"
            style={{ border: "1px solid rgba(0,0,0,0.08)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex shrink-0 items-center justify-between gap-3 border-b p-4"
              style={{ borderColor: "rgba(0,0,0,0.08)" }}
            >
              <span id="file-attachment-title" className="truncate text-sm font-semibold">
                {file.fileName}
              </span>
              <div className="flex shrink-0 items-center gap-3">
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={file.fileName}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#00553E] hover:underline"
                >
                  <Download size={14} /> Télécharger
                </a>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-[6px] text-[#0B1F16]/70 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-2" style={{ background: "#F4F6F5" }}>
              {kind === "image" ? (
                <img
                  src={file.url}
                  alt={file.fileName}
                  className="mx-auto max-h-full max-w-full object-contain"
                />
              ) : kind === "pdf" ? (
                <iframe
                  src={file.url}
                  title={file.fileName}
                  className="h-[75vh] w-full rounded-[8px] border-0 bg-white"
                />
              ) : (
                <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3 p-8 text-center">
                  <FileIcon size={40} className="text-[#0B1F16]/30" />
                  <p className="text-sm text-[#0B1F16]/70">
                    Aperçu non disponible pour ce type de fichier.
                  </p>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={file.fileName}
                    className="inline-flex items-center gap-2 rounded-[6px] px-4 py-2 text-sm font-semibold text-white"
                    style={{ background: "#00553E" }}
                  >
                    <Download size={14} /> Télécharger le fichier
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
